import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Play, Pause, Sparkles, Clock } from 'lucide-react';
import { GlassCard, CardLabel, Pill } from './GlassCard';
import AvaSphere from '../marketing/AvaSphere';

/* ────────────────────────────────────────────────────────────────────
   Hero call card — mirrors the user's reference (dark glass, halo
   pulse, blue→purple gradient sphere with phone glyph). Shows the
   live in-progress call when one exists, otherwise the most recent
   completed call (click to play recording inline).
   ──────────────────────────────────────────────────────────────────── */

export function LiveCallCard() {
  const { api } = useAuth();
  const [active, setActive] = useState(null);
  const [last, setLast] = useState(null);
  const [tick, setTick] = useState(0);
  const [audioOpen, setAudioOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  // Poll every 5s while there's an active call, every 30s otherwise
  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      try {
        const { data } = await api.get('/calls/active');
        if (!alive) return;
        setActive(data.active || null);
        setLast(data.last || null);
      } catch {}
    };
    refresh();
    const id = setInterval(refresh, active ? 5000 : 30000);
    return () => { alive = false; clearInterval(id); };
  }, [api, active]);

  // Tick once per second so the live duration counter advances visibly
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [active]);

  const isLive = !!active;
  const display = active || last;
  const isIdle = !display;

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause(); else audioRef.current.play();
  };

  // Idle phone display when no call data yet
  const phone = isIdle ? 'Ready to take calls' : (display.caller_phone || display.caller_number || 'Unknown');
  const startedAt = !isIdle && display.started_at ? new Date(display.started_at) : null;
  const liveSec = startedAt ? Math.max(0, Math.floor((Date.now() - startedAt.getTime()) / 1000)) : 0;
  void tick; // referenced so the interval re-renders

  return (
    <GlassCard
      data-testid="live-call-card"
      className="relative overflow-hidden p-6 md:p-7 bg-[#0a0c12] border-white/[0.06] dark:bg-[#0a0c12]"
    >
      {/* Background glow */}
      <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[420px] h-[420px] rounded-full bg-gradient-radial from-blue-600/20 to-transparent blur-2xl" />

      <div className="relative flex items-center gap-2 mb-6">
        {isLive ? (
          <>
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-400" />
            </span>
            <span className="text-[10px] uppercase tracking-[0.24em] text-blue-300 font-semibold">Incoming call</span>
          </>
        ) : isIdle ? (
          <>
            <Sparkles className="w-3 h-3 text-blue-400" />
            <span className="text-[10px] uppercase tracking-[0.24em] text-blue-300 font-semibold">Ava · standing by</span>
          </>
        ) : (
          <>
            <Sparkles className="w-3 h-3 text-blue-400" />
            <span className="text-[10px] uppercase tracking-[0.24em] text-blue-300 font-semibold">Last call</span>
          </>
        )}
      </div>

      {/* Ava sphere — same dotted/cyan→purple animated visual used on the marketing site so the dashboard hero stays consistent with the homepage. */}
      <div className="relative flex items-center justify-center my-2 md:my-4">
        <div className="relative w-[220px] h-[220px] md:w-[260px] md:h-[260px] flex items-center justify-center">
          <AvaSphere size={isLive ? 240 : 220} />
        </div>
      </div>

      <div className="relative text-center mt-4">
        <p className="font-heading text-2xl md:text-3xl tabular-nums tracking-wide text-white">{phone}</p>
        <p className="text-xs text-slate-400 mt-1">
          {isLive
            ? <>Ava picked up · {fmtDuration(liveSec)}</>
            : isIdle
              ? <>Your AI receptionist is online and waiting</>
              : <>Ava picked up · {fmtDuration(display.duration_seconds || display.duration_secs || 0)} · {timeAgo(display.ended_at || display.created_at)}</>}
        </p>
      </div>

      {/* Voice waveform — animated when live, static when last */}
      <div className="relative flex items-end justify-center gap-[3px] h-9 mt-5">
        {Array.from({length: 22}).map((_, i) => (
          <span key={i}
            className={`w-[3px] rounded-full bg-blue-400/80 ${isLive ? 'animate-[pulse_1.4s_ease-in-out_infinite]' : ''}`}
            style={{
              height: `${(isLive ? 6 + Math.abs(Math.sin((tick + i) * 0.6)) * 26 : 6 + Math.abs(Math.sin(i * 0.7)) * 22)}px`,
              animationDelay: `${i * 60}ms`,
            }} />
        ))}
      </div>

      {/* Action row */}
      {!isLive && !isIdle && last?.recording_url && (
        <div className="relative mt-5 flex items-center justify-center gap-2">
          <button onClick={() => { setAudioOpen(true); setTimeout(togglePlay, 50); }}
            data-testid="play-last-call"
            className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-full px-4 py-2 inline-flex items-center gap-1.5">
            {playing ? <><Pause className="w-3.5 h-3.5" /> Pause</> : <><Play className="w-3.5 h-3.5" /> Play recording</>}
          </button>
          {last.summary && (
            <Pill tone="purple"><Sparkles className="w-2.5 h-2.5" /> AI summary below</Pill>
          )}
        </div>
      )}

      {audioOpen && last?.recording_url && (
        <audio
          ref={audioRef}
          src={last.recording_url}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          className="relative mt-3 w-full"
          controls
        />
      )}

      {!isLive && !isIdle && last?.summary && (
        <div className="relative mt-4 bg-white/[0.04] border border-white/[0.06] rounded-2xl p-4">
          <CardLabel className="text-slate-500">AI summary</CardLabel>
          <p className="text-sm text-slate-200 leading-relaxed mt-1.5 line-clamp-3">{last.summary}</p>
        </div>
      )}
    </GlassCard>
  );
}

/* Small live banner that sits above content while a call is active */
export function LiveCallBanner() {
  const { api } = useAuth();
  const [active, setActive] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      try {
        const { data } = await api.get('/calls/active');
        if (alive) setActive(data.active || null);
      } catch {}
    };
    refresh();
    const id = setInterval(refresh, active ? 5000 : 20000);
    return () => { alive = false; clearInterval(id); };
  }, [api, active]);

  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [active]);

  if (!active) return null;
  const phone = active.caller_phone || 'Unknown caller';
  const startedAt = active.started_at ? new Date(active.started_at) : null;
  const liveSec = startedAt ? Math.max(0, Math.floor((Date.now() - startedAt.getTime()) / 1000)) : 0;
  void tick;

  return (
    <div data-testid="live-call-banner"
      className="relative overflow-hidden bg-gradient-to-r from-blue-500/15 via-blue-500/10 to-transparent dark:from-blue-500/20 dark:via-blue-500/10 border border-blue-500/30 rounded-2xl px-4 py-3 flex items-center gap-3">
      <span className="relative flex h-2 w-2 flex-shrink-0">
        <span className="absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75 animate-ping" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-400" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-blue-900 dark:text-blue-100">
          <span className="font-semibold">Ava is on a call</span> with <span className="tabular-nums">{phone}</span>
        </p>
        <p className="text-[11px] text-blue-700/80 dark:text-blue-300/80 flex items-center gap-1">
          <Clock className="w-3 h-3" /> {fmtDuration(liveSec)}
        </p>
      </div>
      {/* Mini waveform */}
      <div className="hidden sm:flex items-end gap-[2px] h-5">
        {Array.from({length: 14}).map((_, i) => (
          <span key={i} className="w-[2px] rounded-full bg-blue-500"
            style={{ height: `${4 + Math.abs(Math.sin((tick + i) * 0.7)) * 16}px` }} />
        ))}
      </div>
    </div>
  );
}

function fmtDuration(s) {
  const sec = Math.max(0, s | 0);
  const m = Math.floor(sec / 60);
  const r = sec % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}
function timeAgo(iso) {
  if (!iso) return 'just now';
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return 'just now';
  if (d < 3600) return `${Math.floor(d/60)}m ago`;
  if (d < 86400) return `${Math.floor(d/3600)}h ago`;
  return `${Math.floor(d/86400)}d ago`;
}
